import { ErrorBoundary } from '@sentry/react';
import { useAtomValue } from 'jotai';
import { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { gameIdAtom } from '@/atoms/game/game';
import { unwrappedSelectedGameNameAtom } from '@/atoms/queries/game';
import { unwrappedGameDashboardWidgetsAtom } from '@/atoms/queries/userConfigs';
import { ContentState } from '@/components/contentState/ContentState';
import { NoTextLoader } from '@/components/contentState/NoTextLoader';
import { PageTitle } from '@/components/head/PageTitle';
import { DashboardContent } from '@/components/layout/DashboardContent';
import { GameLayoutWithFilters } from '@/components/layout/game/GameLayoutWithFilters';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { StatusTranslationKey } from '@/types/status';

const GameDashboardContentFallback = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary fallback={<ContentState status={StatusTranslationKey.ERROR} />}>
    <Suspense fallback={<NoTextLoader />}>{children}</Suspense>
  </ErrorBoundary>
);

const GameDashboardWithData = () => {
  const gameId = useAtomValue(gameIdAtom);
  const gameDashboardWidgets = useAtomValue(unwrappedGameDashboardWidgetsAtom);
  if (gameDashboardWidgets === undefined) return <ContentState status={StatusTranslationKey.LOADING} />;
  return <DashboardContent entityId={gameId} dashboardWidgets={gameDashboardWidgets} />;
};

export const GameDashboardPage = () => {
  const { t } = useTranslation('navigation');
  const selectedGameName = useAtomValue(unwrappedSelectedGameNameAtom);
  const page = t('secondary.dashboard');
  const pageTitle = useEntityPageTitle(page, selectedGameName);

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <GameLayoutWithFilters>
        <GameDashboardContentFallback>
          <GameDashboardWithData />
        </GameDashboardContentFallback>
      </GameLayoutWithFilters>
    </>
  );
};
